/* ==========================================================================
   boutiq.experiences — theme script
   Vanilla JS, no dependencies. Four jobs: navigation, variant picker,
   cart drawer, product form.
   ========================================================================== */

(() => {
  'use strict';

  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

  const money = (cents) => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'de', {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'EUR',
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  };

  /* ------------------------------------------------------------ Navigation */

  const navToggle = $('[data-nav-toggle]');
  const nav = $('[data-nav]');
  if (navToggle && nav) {
    navToggle.addEventListener('click', () => {
      const open = nav.classList.toggle('header__nav--open');
      navToggle.setAttribute('aria-expanded', String(open));
    });
  }

  $$('[data-auto-submit]').forEach((el) => {
    el.addEventListener('change', () => el.form?.submit());
  });

  /* -------------------------------------------------------------- Mega menu */

  // Opens on hover and on keyboard focus; on a touch screen the first tap on a
  // menu entry opens the panel instead of following the link, the second tap
  // follows it. Inside the collapsed menu the panel is simply always open.
  (() => {
    const items = $$('[data-mega]');
    if (!items.length) return;

    const collapsed = () => !!$('[data-nav]')?.classList.contains('header__nav--open');
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)');

    const setOpen = (item, open) => {
      const panel = $('[data-mega-panel]', item);
      if (!panel) return;
      item.classList.toggle('is-open', open);
      panel.hidden = !open;
      $('[data-mega-link]', item)?.setAttribute('aria-expanded', String(open));
    };

    const closeAll = (except) => items.forEach((i) => { if (i !== except) setOpen(i, false); });

    items.forEach((item) => {
      const link = $('[data-mega-link]', item);
      link?.setAttribute('aria-expanded', 'false');

      item.addEventListener('mouseenter', () => { if (fine.matches && !collapsed()) { closeAll(item); setOpen(item, true); } });
      item.addEventListener('mouseleave', () => { if (fine.matches && !collapsed()) setOpen(item, false); });

      item.addEventListener('focusin', () => { closeAll(item); setOpen(item, true); });
      item.addEventListener('focusout', (e) => {
        if (!item.contains(e.relatedTarget)) setOpen(item, false);
      });

      link?.addEventListener('click', (e) => {
        if (fine.matches || collapsed()) return;       // mouse: follow the link
        if (item.classList.contains('is-open')) return; // second tap: follow it
        e.preventDefault();
        closeAll(item);
        setOpen(item, true);
      });
    });

    document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !collapsed()) closeAll(); });
    document.addEventListener('click', (e) => {
      // In the collapsed menu the panels stay open — otherwise the tap on the
      // menu button itself would close what it just opened.
      if (collapsed()) return;
      if (!e.target.closest('[data-mega]')) closeAll();
    });

    // The collapsed menu shows everything; there is no hover there.
    const nav = $('[data-nav]');
    if (nav) {
      new MutationObserver(() => {
        const open = nav.classList.contains('header__nav--open');
        items.forEach((i) => setOpen(i, open));
      }).observe(nav, { attributes: true, attributeFilter: ['class'] });
    }
  })();

  /* --------------------------------------------------------- Cart drawer */

  const drawer = $('[data-cart-drawer]');
  let lastFocus = null;

  const openDrawer = () => {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    $('[data-cart-close]', drawer)?.focus();
  };

  const closeDrawer = () => {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    lastFocus?.focus();
  };

  $$('[data-cart-toggle]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const href = btn.dataset.cartHref;
      if (href) { window.location.href = href; return; }
      openDrawer();
    });
  });

  $$('[data-cart-close]').forEach((el) => el.addEventListener('click', closeDrawer));
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && drawer?.classList.contains('is-open')) closeDrawer();
  });

  // Keep the focus inside the open drawer
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Tab' || !drawer?.classList.contains('is-open')) return;
    const focusables = $$('a[href], button:not([disabled]), input, textarea, select', drawer)
      .filter((el) => el.offsetParent !== null);
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  });

  /* ---------------------------------------------------------- Cart state */

  const renderCart = async () => {
    const [itemsHtml, footHtml, cartJson] = await Promise.all([
      fetch(`${window.Shopify?.routes?.root || '/'}?section_id=cart-fragment-items`).then((r) => (r.ok ? r.text() : null)).catch(() => null),
      null,
      fetch(`${window.Shopify?.routes?.root || '/'}cart.js`).then((r) => r.json()),
    ]);

    $$('[data-cart-count]').forEach((el) => { el.textContent = cartJson.item_count; });

    // When section rendering is unavailable, reload the page rather than
    // showing half a state.
    if (!itemsHtml) { window.location.reload(); return; }

    const doc = new DOMParser().parseFromString(itemsHtml, 'text/html');
    const body = $('[data-cart-body]');
    const foot = $('[data-cart-foot]');
    const newBody = $('[data-cart-body-source]', doc);
    const newFoot = $('[data-cart-foot-source]', doc);
    if (body && newBody) body.innerHTML = newBody.innerHTML;
    if (foot && newFoot) foot.innerHTML = newFoot.innerHTML;
    bindCartEvents();
  };

  const changeLine = async (line, quantity) => {
    const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
    });
    if (!res.ok) return;
    await renderCart();
  };

  function bindCartEvents() {
    $$('[data-line-remove]').forEach((btn) => {
      btn.addEventListener('click', () => changeLine(btn.dataset.line, 0));
    });
    $$('[data-qty-input]').forEach((input) => {
      input.addEventListener('change', () => changeLine(input.dataset.line, input.value));
    });
    $$('[data-qty-up]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('[data-qty-input]');
        input.value = Number(input.value) + 1;
        input.dispatchEvent(new Event('change'));
      });
    });
    $$('[data-qty-down]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const input = btn.parentElement.querySelector('[data-qty-input]');
        input.value = Math.max(0, Number(input.value) - 1);
        input.dispatchEvent(new Event('change'));
      });
    });
    const note = $('[data-cart-note]');
    if (note) {
      let t;
      note.addEventListener('input', () => {
        clearTimeout(t);
        t = setTimeout(() => {
          fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ note: note.value }),
          });
        }, 600);
      });
    }
  }
  bindCartEvents();

  /* --------------------------------------------------------- Product form */

  // The form element itself carries no custom attributes: Shopify's {% form %}
  // tag is fussy about them, so the wrapper holds the settings and the class
  // identifies the form.
  $$('form.product-form, [data-product-form]').forEach((form) => {
    // A booking app (Cowlendar and the like) drives the form itself: adds its
    // date fields, validates, sometimes submits. Intercepting that with our
    // own fetch would drop what it collected, so we stay out of the way.
    if (form.closest('[data-native-submit]')) return;

    form.addEventListener('submit', async (e) => {
      if (!drawer) return; // without the drawer, a plain POST to /cart/add
      e.preventDefault();
      const btn = $('[data-add-to-cart]', form);
      const original = btn?.textContent;
      if (btn) { btn.disabled = true; btn.textContent = btn.dataset.loadingText || '…'; }

      const res = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      if (btn) { btn.disabled = false; btn.textContent = original; }

      const errorEl = $('[data-form-error]', form);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (errorEl) { errorEl.textContent = err.description || err.message || ''; errorEl.hidden = false; }
        return;
      }
      if (errorEl) errorEl.hidden = true;

      await renderCart();
      const wrap = form.closest('[data-auto-open]');
      const autoOpen = form.dataset.autoOpen ?? wrap?.dataset.autoOpen;
      if (autoOpen !== 'false') openDrawer();
    });
  });

  /* -------------------------------------------------------- Variant picker */

  customElements.define(
    'variant-picker',
    class VariantPicker extends HTMLElement {
      connectedCallback() {
        this.priceTarget = document.querySelector('[data-pdp-price]');
        this.submit = document.querySelector('[data-add-to-cart]');

        // Terminliste: Radiobuttons
        this.addEventListener('change', (e) => {
          if (e.target.name === 'id') this.onVariantChange(e.target.dataset.price, !e.target.disabled);
        });

        // Selects: Optionswerte auf Variante abbilden
        const hidden = this.querySelector('[data-variant-select]');
        const dataEl = this.querySelector('[data-variant-data]');
        if (!hidden || !dataEl) return;

        this.variants = JSON.parse(dataEl.textContent);
        this.selects = Array.from(this.querySelectorAll('[data-option-index]'));
        this.selects.forEach((sel) => sel.addEventListener('change', () => this.match()));
      }

      match() {
        const chosen = this.selects.map((s) => s.value);
        const variant = this.variants.find((v) =>
          chosen.every((val, i) => v.options[i] === val)
        );
        const hidden = this.querySelector('[data-variant-select]');
        if (!variant) { this.onVariantChange(null, false); return; }
        hidden.value = variant.id;
        this.onVariantChange(null, variant.available, variant.price);
        const url = new URL(window.location);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url);
      }

      onVariantChange(priceText, available, priceCents) {
        if (this.priceTarget) {
          if (priceText) this.priceTarget.textContent = priceText;
          else if (typeof priceCents === 'number') this.priceTarget.textContent = money(priceCents);
        }
        if (this.submit) {
          this.submit.disabled = !available;
          this.submit.textContent = available
            ? this.submit.dataset.labelAvailable
            : this.submit.dataset.labelSoldOut;
        }
      }
    }
  );


  /* --------------------------------------------------------------- Finder */

  // Scored rather than filtered: matches count up, contradictions count down,
  // missing data is neutral. A catalogue with no maintained tags still yields
  // a sensible order instead of an empty result list.
  const SCORE_HIT = 3;
  const SCORE_MISS = -2;

  // Ranking alone is not enough. If an answer is contradicted by hard data —
  // a price outside the budget, no date in the chosen month — the journey has
  // to be labelled as such, otherwise the finder looks as if it ignored the
  // question. Only the keyword runs on free text, which is too patchy to
  // exclude anything on; it ranks, it never disqualifies.
  const HARD_FIELDS = ['price', 'month', 'tag', 'type', 'shipping'];

  // Dates appear in variant titles in wildly different spellings:
  // "06/02/2027-13/02/2027", "17.05.2026 - 24.05.2026", "30.03. - 05.04.2026".
  // So we only look for day-month pairs; the year is optional.
  const monthsOf = (variants) => {
    const out = new Set();
    (variants || []).forEach((v) => {
      const re = /\b(\d{1,2})[.\/](\d{1,2})(?:[.\/](\d{2,4}))?/g;
      let m;
      while ((m = re.exec(v))) {
        const mo = Number(m[2]);
        if (mo >= 1 && mo <= 12) out.add(mo);
      }
    });
    return out;
  };

  const listOf = (raw) => String(raw || '').split(',').map((s) => s.trim().toLowerCase()).filter(Boolean);

  // A price range is typed by hand in the theme editor, so it arrives in every
  // spelling a person might use: "500-1500", "3500-", "€500 – €1.500". The
  // en dash in particular comes for free from autocorrect, and splitting on a
  // plain hyphen would silently turn the whole range into "no limit" — the
  // filter would look broken while reporting nothing. So: normalise the dash,
  // drop currency symbols and thousand separators, then read the numbers.
  // Anything that still cannot be read counts as no answer, never as a match.
  const numberFrom = (raw) => {
    const cleaned = String(raw)
      .replace(/[^\d.,]/g, '')
      .replace(/[.,](?=\d{3}(\D|$))/g, '')
      .replace(',', '.');
    if (cleaned === '') return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  const priceRange = (raw) => {
    const parts = String(raw).replace(/[\u2010-\u2015\u2212]/g, '-').split('-');
    const lo = numberFrom(parts[0]);
    const hi = parts.length > 1 ? numberFrom(parts[1]) : null;

    // A single number with no separator reads as an upper limit — that is how
    // people write it, and "from X" would be the surprising reading.
    if (parts.length === 1) return lo === null ? null : { min: 0, max: lo };
    if (lo === null && hi === null) return null;
    return { min: lo === null ? 0 : lo, max: hi === null ? Infinity : hi };
  };

  customElements.define(
    'journey-finder',
    class JourneyFinder extends HTMLElement {
      connectedCallback() {
        const dataEl = $('[data-finder-data]', this);
        if (!dataEl) return;
        try { this.products = JSON.parse(dataEl.textContent); } catch { return; }
        this.products.forEach((p) => { p.months = monthsOf(p.variants); });

        this.steps = $$('.finder__step', this);
        this.panel = $('.finder__panel', this);
        this.results = $('.finder__results', this);
        this.grid = $('[data-finder-grid]', this);
        this.items = $$('[data-finder-item]', this);
        this.summary = $('[data-finder-summary]', this);
        this.backBtn = $('[data-finder-back]', this);
        this.skipBtn = $('[data-finder-skip]', this);
        this.noMatch = $('[data-finder-nomatch]', this);
        this.empty = $('[data-finder-empty]', this);
        this.limit = Number(this.dataset.results) || 3;
        this.answers = [];
        this.at = 0;

        this.addEventListener('click', (e) => {
          const opt = e.target.closest('.finder__option');
          if (opt) { this.answer(opt); return; }
          if (e.target.closest('[data-finder-skip]')) { this.answer(null); return; }
          if (e.target.closest('[data-finder-back]')) { this.back(); return; }
          if (e.target.closest('[data-finder-restart]')) { this.restart(); return; }
        });
      }

      answer(btn) {
        const step = this.steps[this.at];
        if (btn) {
          $$('.finder__option', step).forEach((b) => b.setAttribute('aria-pressed', 'false'));
          btn.setAttribute('aria-pressed', 'true');
        }
        this.answers[this.at] = btn
          ? { field: step.dataset.field, value: btn.dataset.value, label: btn.textContent.trim() }
          : null;
        this.at += 1;
        if (this.at >= this.steps.length) this.finish();
        else this.show();
      }

      back() {
        if (this.at === 0) return;
        this.at -= 1;
        this.answers[this.at] = null;
        this.show();
      }

      restart() {
        this.at = 0;
        this.answers = [];
        $$('.finder__option', this).forEach((b) => b.setAttribute('aria-pressed', 'false'));
        this.results.hidden = true;
        this.panel.hidden = false;
        this.show();
        this.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }

      show() {
        this.steps.forEach((s, i) => { s.hidden = i !== this.at; });
        this.backBtn.hidden = this.at === 0;
      }

      // Which of the given answers this journey contradicts on hard data.
      missed(p) {
        return this.answers.filter(
          (a) => a && a.value && HARD_FIELDS.includes(a.field) && this.score(a, p) === SCORE_MISS
        );
      }

      // One step against one product.
      // Returns SCORE_HIT, SCORE_MISS or 0 (no data / doesn't matter).
      score(answer, p) {
        if (!answer || !answer.value) return 0;
        const want = listOf(answer.value);

        switch (answer.field) {
          case 'month': {
            if (!p.months.size) return 0;
            return want.some((w) => p.months.has(Number(w))) ? SCORE_HIT : SCORE_MISS;
          }
          case 'price': {
            const range = priceRange(answer.value);
            if (!range) return 0;
            return p.price >= range.min && p.price <= range.max ? SCORE_HIT : SCORE_MISS;
          }
          case 'tag': {
            const tags = (p.tags || []).map((t) => t.toLowerCase());
            if (!tags.length) return 0;
            return want.some((w) => tags.includes(w)) ? SCORE_HIT : SCORE_MISS;
          }
          case 'type': {
            if (!p.type) return 0;
            return want.includes(p.type) ? SCORE_HIT : SCORE_MISS;
          }
          case 'shipping': {
            const wants = String(answer.value).toLowerCase() === 'true';
            return p.shipping === wants ? SCORE_HIT : SCORE_MISS;
          }
          default: {
            // Keyword: a hit in the text counts, a miss is judged more mildly
            // — free text is patchier than a maintained tag.
            const hit = want.some((w) => p.text.includes(w));
            return hit ? SCORE_HIT : -1;
          }
        }
      }

      finish() {
        const scored = this.products.map((p) => ({
          p,
          missed: this.missed(p),
          s: this.answers.reduce((sum, a) => sum + this.score(a, p), 0) + (p.available ? 0.5 : 0),
        }));

        const byScore = (a, b) => b.s - a.s;
        const exact = scored.filter((r) => r.missed.length === 0).sort(byScore);
        const near = scored
          .filter((r) => r.missed.length > 0)
          .sort((a, b) => a.missed.length - b.missed.length || byScore(a, b));

        // Fill up with near misses so the finder never dead-ends — but every
        // one of them says what does not fit.
        const shown = exact.slice(0, this.limit);
        if (shown.length < this.limit) shown.push(...near.slice(0, this.limit - shown.length));

        this.items.forEach((el) => {
          el.hidden = true;
          el.style.order = '';
          const note = $('[data-finder-miss]', el);
          if (note) { note.hidden = true; note.textContent = ''; }
        });

        shown.forEach((r, rank) => {
          const el = this.items[r.p.i];
          if (!el) return;
          el.hidden = false;
          el.style.order = rank;
          const note = $('[data-finder-miss]', el);
          if (note && r.missed.length) {
            const labels = r.missed.map((a) => a.label).join(', ');
            note.textContent = [note.dataset.prefix, labels].filter(Boolean).join(' ');
            note.hidden = false;
          }
        });

        if (this.noMatch) this.noMatch.hidden = exact.length > 0 || shown.length === 0;
        if (this.empty) this.empty.hidden = shown.length > 0;

        const picked = this.answers.filter(Boolean).map((a) => a.label);
        this.summary.textContent = picked.length
          ? picked.join(' · ')
          : (this.summary.dataset.fallback || '');

        this.panel.hidden = true;
        this.results.hidden = false;
        this.results.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }
    }
  );


  /* --------------------------------------------------------- Jump bar */

  /* Marks the section currently in view. Only an aid — the links work on
     their own, so nothing breaks if this never runs. */

  $$('.jump').forEach((bar) => {
    const links = $$('a[href^="#"]', bar);
    const targets = links
      .map((a) => ({ a, el: document.getElementById(a.getAttribute('href').slice(1)) }))
      .filter((t) => t.el);
    if (targets.length < 2) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (!e.isIntersecting) return;
          links.forEach((a) => a.classList.toggle('is-current', a.getAttribute('href') === '#' + e.target.id));
        });
      },
      { rootMargin: '-20% 0px -70% 0px' }
    );
    targets.forEach((t) => io.observe(t.el));
  });

  /* ------------------------------------------------- Metafield rich text */

  /* Nina keeps "what's included", "what's not included" and the optional
     add-ons in ONE rich text metafield, separated by headings. Splitting that
     in Liquid means string surgery on HTML; doing it here means working with
     real nodes, and if anything is unexpected the original field is simply
     shown as it was written. Nothing is ever lost, only laid out better. */

  const isHeadingNode = (el) => {
    if (/^H[1-4]$/.test(el.tagName)) return true;
    // The rich text editor also produces a paragraph that is entirely bold —
    // people use that as a heading and it looks like one.
    if (el.tagName !== 'P') return false;
    const strong = el.querySelector('strong, b');
    return !!strong && el.textContent.trim() === strong.textContent.trim() && el.textContent.trim() !== '';
  };

  // Shopify's metafield_tag wraps rich text in a div of its own, so step
  // through single wrappers until the real paragraphs and lists are in hand.
  const contentRoot = (root) => {
    let node = root;
    while (node.children.length === 1 && /^(DIV|SECTION)$/.test(node.children[0].tagName)) {
      node = node.children[0];
    }
    return node;
  };

  const splitGroups = (rootEl) => {
    const root = contentRoot(rootEl);
    const groups = [];
    let current = null;
    Array.from(root.children).forEach((el) => {
      if (isHeadingNode(el)) {
        current = { heading: el.textContent.trim(), items: [], notes: [] };
        groups.push(current);
        return;
      }
      if (!current) {
        current = { heading: '', items: [], notes: [] };
        groups.push(current);
      }
      if (el.tagName === 'UL' || el.tagName === 'OL') {
        Array.from(el.children).forEach((li) => {
          const t = li.textContent.trim();
          if (t) current.items.push(t);
        });
      } else {
        const t = el.textContent.trim();
        if (t) current.notes.push(t);
      }
    });
    return groups.filter((g) => g.items.length || g.notes.length);
  };

  // A heading can carry a parenthetical note: "What's included (per person)".
  const splitHeading = (heading) => {
    const m = heading.match(/^(.*?)\s*[（(]([^)）]*)[)）]\s*$/);
    return m ? { title: m[1].trim(), note: m[2].trim() } : { title: heading, note: '' };
  };

  const groupKind = (heading) => {
    if (/not\s*includ|exclud|nicht\s*(ent|inbe)/i.test(heading)) return 'excluded';
    if (/add[-\s]?on|optional|zusatz|extras?/i.test(heading)) return 'addon';
    return 'included';
  };

  /* Icons are a help, not a decoration: one is shown only where a keyword is
     unmistakable. Everywhere else the slot stays empty so the lines still
     align. A wrong icon costs more trust than a missing one. */
  const ICON_RULES = [
    [/helicopter|heli\b|flight hour|charter/i, 'helicopter'],
    [/guide|guiding/i, 'guide'],
    [/yoga|recovery|wellness|spa|therap|massage/i, 'spa'],
    [/lift|gondola|cable car/i, 'lift'],
    [/accommodation|lodge|hotel|chalet|room|nights?\b/i, 'key'],
    [/chef|gourmet|hospitality|catering|breakfast|full board|half board/i, 'chef'],
    [/meal|dinner|lunch|drinks|winery|restaurant|table/i, 'dining'],
    [/equipment|rental|ski\b|snowboard|touring gear/i, 'ski'],
    [/transfer|shuttle|pick[-\s]?up|door to door/i, 'transfer'],
    [/avalanche|safety|airbag|transceiver|insurance/i, 'shield'],
    [/international flights?|airport|airline|plane/i, 'plane'],
    [/video|film|camera|photo|production/i, 'camera'],
    [/city|tour|santiago|valpara|excursion|sightseeing/i, 'map-pin'],
    [/famil(y|ies)|children|kids/i, 'family'],
    [/book|print/i, 'book'],
    [/concierge|logistics/i, 'concierge'],
  ];
  const iconNameFor = (line) => {
    const hit = ICON_RULES.find(([re]) => re.test(line));
    return hit ? hit[1] : null;
  };

  customElements.define(
    'spec-columns',
    class SpecColumns extends HTMLElement {
      connectedCallback() {
        const src = $('[data-spec-source]', this);
        const target = $('[data-spec-target]', this);
        if (!src || !target) return;

        const groups = splitGroups(src);

        // One group and no headings means the field is a plain list. Laying
        // that out in columns would invent a structure that is not there.
        if (groups.length < 2) {
          src.hidden = false;
          return;
        }

        const sprite = $('[data-spec-icons]', this);
        const iconMarkup = (name) => {
          const node = sprite && sprite.querySelector('[data-icon="' + name + '"]');
          return node ? node.innerHTML : '';
        };

        target.innerHTML = groups
          .map((g) => {
            const kind = groupKind(g.heading);
            const { title, note } = splitHeading(g.heading);
            const items = g.items
              .map((line) => {
                const name = kind === 'excluded' ? null : iconNameFor(line);
                const mark = name ? iconMarkup(name) : '';
                return (
                  '<li class="speclist__item">' +
                  '<span class="speclist__icon"' + (mark ? '' : ' aria-hidden="true"') + '>' + mark + '</span>' +
                  '<span>' + line + '</span></li>'
                );
              })
              .join('');
            const notes = g.notes.map((t) => '<p class="price__note">' + t + '</p>').join('');
            return (
              '<div class="speclist speclist--' + kind + '">' +
              (title ? '<h3 class="speclist__head">' + title + '</h3>' : '') +
              (note ? '<p class="price__note speclist__note">' + note + '</p>' : '') +
              notes +
              (items ? '<ul class="speclist__items">' + items + '</ul>' : '') +
              '</div>'
            );
          })
          .join('');

        target.hidden = false;
        this.classList.add('is-split');
      }
    }
  );

  /* The highlights field, turned into a row of facts. Same source shape, but
     everything is one flat list — headings are ignored. */

  customElements.define(
    'fact-row',
    class FactRow extends HTMLElement {
      connectedCallback() {
        const src = $('[data-spec-source]', this);
        const target = $('[data-spec-target]', this);
        if (!src || !target) return;

        const groups = splitGroups(src);
        let items = groups.flatMap((g) => g.items);

        // No list in the field: fall back to a paragraph split on commas.
        if (!items.length) {
          items = groups
            .flatMap((g) => g.notes)
            .join(', ')
            .split(/,|·|•/)
            .map((t) => t.trim())
            .filter(Boolean);
        }

        const max = Number(this.dataset.max || 6);
        items = items.filter((t) => t.length <= Number(this.dataset.maxLength || 40)).slice(0, max);
        if (!items.length) { src.hidden = false; return; }

        const sprite = $('[data-spec-icons]', this);
        target.innerHTML = items
          .map((line) => {
            const name = iconNameFor(line);
            const node = name && sprite && sprite.querySelector('[data-icon="' + name + '"]');
            return (
              '<span class="facts__item">' +
              (node ? node.innerHTML : '') +
              '<span>' + line + '</span></span>'
            );
          })
          .join('');
        target.hidden = false;
      }
    }
  );

  /* -------------------------------------------------------------- Gallery */

  /* The product gallery. Every image is already in the stage; switching only
     changes which one is visible, so nothing is fetched on a click. Works by
     click, by arrow key, and by swiping on a phone. The thumbnail strip
     scrolls the active image into view so it never sits off-screen. */

  customElements.define(
    'product-gallery',
    class ProductGallery extends HTMLElement {
      connectedCallback() {
        this.slides = $$('[data-pdp-slide]', this);
        if (this.slides.length < 2) return;
        this.thumbs = $$('[data-pdp-thumb]', this);
        this.strip = $('[data-pdp-thumbs]', this);
        this.counter = $('[data-pdp-counter]', this);
        this.at = 0;

        this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
        $('[data-pdp-prev]', this)?.addEventListener('click', () => this.go(this.at - 1));
        $('[data-pdp-next]', this)?.addEventListener('click', () => this.go(this.at + 1));

        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') { e.preventDefault(); this.go(this.at - 1); }
          if (e.key === 'ArrowRight') { e.preventDefault(); this.go(this.at + 1); }
        });

        // Swipe on the large image
        let x0 = null;
        let y0 = null;
        const stage = $('.pdp__stage', this) || this;
        stage.addEventListener('touchstart', (e) => {
          x0 = e.touches[0].clientX;
          y0 = e.touches[0].clientY;
        }, { passive: true });
        stage.addEventListener('touchend', (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          const dy = e.changedTouches[0].clientY - y0;
          // Only react to a horizontal swipe — otherwise scrolling the page
          // would flick through the images.
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.go(this.at + (dx < 0 ? 1 : -1));
          x0 = null;
          y0 = null;
        }, { passive: true });

        this.go(0);
      }

      go(i) {
        const n = this.slides.length;
        this.at = ((i % n) + n) % n;

        this.slides.forEach((el, k) => {
          const on = k === this.at;
          el.classList.toggle('is-current', on);
          el.setAttribute('aria-hidden', String(!on));
        });

        this.thumbs.forEach((t, k) => t.setAttribute('aria-current', String(k === this.at)));

        const active = this.thumbs[this.at];
        if (active && this.strip && this.strip.scrollWidth > this.strip.clientWidth) {
          const left = active.offsetLeft - (this.strip.clientWidth - active.offsetWidth) / 2;
          this.strip.scrollTo({ left, behavior: 'smooth' });
        }

        if (this.counter) this.counter.textContent = `${this.at + 1} / ${n}`;
      }
    }
  );

  /* ---------------------------------------------------------- Video sound */
  /* Banners start muted, or the browser blocks the autoplay. The button
     lifts the mute and silences every other running video at the same time
     — there should never be two soundtracks at once. */

  const soundBoxes = $$('[data-video]');
  soundBoxes.forEach((box) => {
    const btn = $('[data-video-sound]', box);
    const media = $('video', box);
    if (!btn || !media) return;

    const paint = () => {
      const on = !media.muted;
      btn.setAttribute('aria-pressed', String(on));
      $$('[data-when]', btn).forEach((el) => {
        el.hidden = el.dataset.when !== (on ? 'on' : 'off');
      });
    };

    btn.addEventListener('click', () => {
      const turnOn = media.muted;
      if (turnOn) {
        soundBoxes.forEach((other) => {
          if (other === box) return;
          const m = $('video', other);
          if (m && !m.muted) {
            m.muted = true;
            other.dispatchEvent(new CustomEvent('video:muted'));
          }
        });
      }
      media.muted = !turnOn;
      if (turnOn && media.paused) media.play().catch(() => {});
      paint();
    });

    box.addEventListener('video:muted', paint);
    media.addEventListener('volumechange', paint);
    paint();
  });

  /* ----------------------------------------------------------- Accordion */
  /* <details>/<summary> does the work; this only makes sure the others close
     when "only one open at a time" is on. */

  $$('[data-accordion]').forEach((group) => {
    if (group.dataset.accordion !== 'single') return;
    const items = $$('details', group);
    items.forEach((item) => {
      item.addEventListener('toggle', () => {
        if (!item.open) return;
        items.forEach((other) => {
          if (other !== item) other.open = false;
        });
      });
    });
  });

  /* ----------------------------------------------------------- Slideshow */

  customElements.define(
    'slide-show',
    class SlideShow extends HTMLElement {
      connectedCallback() {
        this.slides = $$('[data-slide]', this);
        if (this.slides.length < 2) return;
        this.dots = $$('[data-slide-dot]', this);
        this.index = 0;
        this.interval = Number(this.dataset.autoplay || 0) * 1000;

        $('[data-slide-prev]', this)?.addEventListener('click', () => this.go(this.index - 1, true));
        $('[data-slide-next]', this)?.addEventListener('click', () => this.go(this.index + 1, true));
        this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i, true)));

        this.addEventListener('keydown', (e) => {
          if (e.key === 'ArrowLeft') this.go(this.index - 1, true);
          if (e.key === 'ArrowRight') this.go(this.index + 1, true);
        });

        // Swipe on a phone
        let x0 = null;
        this.addEventListener('touchstart', (e) => { x0 = e.touches[0].clientX; }, { passive: true });
        this.addEventListener('touchend', (e) => {
          if (x0 === null) return;
          const dx = e.changedTouches[0].clientX - x0;
          if (Math.abs(dx) > 40) this.go(this.index + (dx < 0 ? 1 : -1), true);
          x0 = null;
        }, { passive: true });

        this.addEventListener('mouseenter', () => this.stop());
        this.addEventListener('mouseleave', () => this.start());
        this.addEventListener('focusin', () => this.stop());

        this.go(0);
        this.start();
      }

      disconnectedCallback() { this.stop(); }

      go(i, manual) {
        const n = this.slides.length;
        this.index = ((i % n) + n) % n;
        this.slides.forEach((s, k) => {
          const on = k === this.index;
          s.classList.toggle('is-current', on);
          s.setAttribute('aria-hidden', String(!on));
          $$('a, button', s).forEach((el) => el.setAttribute('tabindex', on ? '0' : '-1'));
        });
        this.dots.forEach((d, k) => d.setAttribute('aria-current', String(k === this.index)));
        if (manual) this.restart();
      }

      start() {
        if (!this.interval) return;
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        this.timer = setInterval(() => this.go(this.index + 1), this.interval);
      }
      stop() { clearInterval(this.timer); }
      restart() { this.stop(); this.start(); }
    }
  );

  /* ------------------------------------------------------------------- FAQ */
  // Questions and answers written as one rich text field: a bold line is a
  // question, everything until the next bold line is its answer. The same
  // shape works for a product metafield and for the standalone FAQ page.

  customElements.define(
    'faq-list',
    class FaqList extends HTMLElement {
      connectedCallback() {
        const src = this.querySelector('[data-spec-source]');
        const target = this.querySelector('[data-spec-target]');
        if (!src || !target) return;

        const root = contentRoot(src);
        const pairs = [];
        let current = null;

        Array.from(root.children).forEach((el) => {
          if (isHeadingNode(el)) {
            current = { q: el.textContent.trim(), a: [] };
            pairs.push(current);
            return;
          }
          if (current) current.a.push(el.cloneNode(true));
        });

        const usable = pairs.filter((p) => p.q && p.a.length);
        if (!usable.length) {
          // Not the expected shape — show the text as it was written.
          src.hidden = false;
          return;
        }

        const icon = this.querySelector('[data-faq-mark]');
        target.dataset.accordion = this.dataset.single === 'true' ? 'single' : 'multi';

        usable.forEach((pair) => {
          const item = document.createElement('details');
          item.className = 'accordion__item';

          const head = document.createElement('summary');
          head.className = 'accordion__head';
          const q = document.createElement('span');
          q.className = 'accordion__q';
          q.textContent = pair.q;
          head.append(q);
          const mark = document.createElement('span');
          mark.className = 'accordion__mark';
          if (icon) mark.innerHTML = icon.innerHTML;
          head.append(mark);

          const body = document.createElement('div');
          body.className = 'accordion__body rte';
          pair.a.forEach((node) => body.append(node));

          item.append(head, body);
          target.append(item);
        });

        src.hidden = true;
        target.hidden = false;
      }
    }
  );

  /* ------------------------------------------------------- Withdrawal form */

  // The form sits behind its own button so the page does not open with a legal
  // form in the reader's face. Without JS the button is hidden and the form is
  // simply there — the declaration must never depend on a script.
  customElements.define(
    'withdrawal-form',
    class WithdrawalForm extends HTMLElement {
      connectedCallback() {
        const toggle = this.querySelector('[data-withdrawal-toggle]');
        const body = this.querySelector('[data-withdrawal-body]');
        if (!toggle || !body) return;

        // A submitted form reopens itself so the message is visible.
        const posted = !!body.querySelector('.notice');
        const open = (on) => {
          body.hidden = !on;
          toggle.setAttribute('aria-expanded', String(on));
          this.querySelector('[data-withdrawal-open]').hidden = on;
        };
        open(posted);

        toggle.addEventListener('click', () => {
          open(true);
          body.querySelector('input, textarea')?.focus();
        });
      }
    }
  );
})();
